/**
 * Utilitario para manipulacao de arquivos CSV.
 * Funcoes compartilhadas entre servicos de cloud storage.
 */

/**
 * Remove BOM (Byte Order Mark) e normaliza quebras de linha.
 * @param {string} content - Conteudo do arquivo
 * @returns {string} Conteudo sanitizado
 */
export function sanitizeContent(content) {
  let sanitized = content;

  if (sanitized.charCodeAt(0) === 0xfeff) {
    sanitized = sanitized.slice(1);
  }

  sanitized = sanitized.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  sanitized = sanitized.trimEnd();

  return sanitized;
}

/**
 * Detecta o delimitador mais provavel do CSV analisando as primeiras linhas.
 * @param {string} csvContent - Conteudo CSV
 * @returns {string} Delimitador detectado (,  ; \t ou |)
 */
export function detectDelimiter(csvContent) {
  const firstLines = csvContent.split('\n').slice(0, 5).join('\n');

  const delimiters = [
    { char: ',', count: (firstLines.match(/,/g) || []).length },
    { char: ';', count: (firstLines.match(/;/g) || []).length },
    { char: '\t', count: (firstLines.match(/\t/g) || []).length },
    { char: '|', count: (firstLines.match(/\|/g) || []).length },
  ];

  delimiters.sort((a, b) => b.count - a.count);

  if (delimiters[0].count > 0) {
    return delimiters[0].char;
  }

  return ',';
}

/**
 * Faz parse de uma linha CSV respeitando campos entre aspas.
 * @param {string} line - Linha CSV
 * @param {string} delimiter - Delimitador usado
 * @returns {string[]} Array de campos
 */
export function parseCSVLine(line, delimiter) {
  const fields = [];
  let currentField = '';
  let insideQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    const nextChar = line[i + 1];

    if (char === '"') {
      if (insideQuotes && nextChar === '"') {
        currentField += '"';
        i++;
      } else {
        insideQuotes = !insideQuotes;
      }
    } else if (char === delimiter && !insideQuotes) {
      fields.push(currentField);
      currentField = '';
    } else {
      currentField += char;
    }
  }

  fields.push(currentField);
  return fields;
}

/**
 * Converte array de campos para linha CSV, escapando quando necessario.
 * @param {string[]} fields - Campos
 * @param {string} delimiter - Delimitador a usar
 * @returns {string} Linha CSV
 */
export function fieldsToCSVLine(fields, delimiter) {
  return fields
    .map((field) => {
      const needsQuotes =
        field.includes(delimiter) ||
        field.includes('\n') ||
        field.includes('\r') ||
        field.includes('"');

      if (needsQuotes) {
        const escaped = field.replace(/"/g, '""');
        return `"${escaped}"`;
      }

      return field;
    })
    .join(delimiter);
}

/**
 * Normaliza CSV para usar ponto-e-virgula como delimitador padrao.
 * @param {string} csvContent - Conteudo CSV original
 * @returns {string} CSV normalizado com ; como delimitador
 */
export function normalizeCSV(csvContent) {
  const delimiter = detectDelimiter(csvContent);
  const lines = csvContent.split('\n');
  const targetDelimiter = ';';

  const normalizedLines = lines.map((line) => {
    if (!line.trim()) return line;

    const fields = parseCSVLine(line, delimiter);
    return fieldsToCSVLine(fields, targetDelimiter);
  });

  return normalizedLines.join('\n');
}

/**
 * Converte string CSV para objeto File.
 * @param {string} csvContent - Conteudo CSV
 * @param {string} fileName - Nome do arquivo
 * @returns {File} Objeto File
 */
export function csvToFile(csvContent, fileName = 'imported-file.csv') {
  const normalizedContent = normalizeCSV(csvContent);
  const blob = new Blob([normalizedContent], { type: 'text/csv' });
  return new File([blob], fileName, { type: 'text/csv' });
}

/**
 * Normaliza um arquivo CSV carregado pelo usuario.
 * @param {File} file - Arquivo CSV
 * @returns {Promise<File>} Arquivo normalizado
 */
export function normalizeUploadedFile(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (event) => {
      try {
        const content = event.target.result;
        const sanitized = sanitizeContent(content);
        const normalizedContent = normalizeCSV(sanitized);
        const blob = new Blob([normalizedContent], { type: 'text/csv' });
        const normalizedFile = new File([blob], file.name, { type: 'text/csv' });
        resolve(normalizedFile);
      } catch (error) {
        reject(new Error('Erro ao processar arquivo CSV'));
      }
    };

    reader.onerror = () => {
      reject(new Error('Erro ao ler arquivo'));
    };

    reader.readAsText(file, 'UTF-8');
  });
}
